<script setup>
import { subs_time, formattedDate, swalError, swalSuccess } from '@/helper';
import { retrieve_child } from '@/services/child';
import { get_subscription, activate_child_subscription } from '@/services/subscriptions';
import { computed, onMounted, ref } from 'vue';

const props = defineProps({
  child_id: {
    required: true
  }
})

const isLoading = ref(true)
const child = ref({})
const activatingId = ref(null)

onMounted(async()=>{
  await get_child_subscription()
  isLoading.value = false
})

async function get_child_subscription(){
  let response = await retrieve_child(props.child_id, '?subscription=true&subscription_data=true')
  if (response.error){
    swalError(response.result, true)
    return
  }
  if (response.result.data.included?.length != 0){
    child.value = parseSubscription(response.result.data)
    isEmpty.value = false
  }
}

function parseSubscription(result){
  result = result.child
  let tempChild = result.data
  let child_subscriptions = result.included.filter(item => item.type == 'child_subscription')
  child_subscriptions.forEach(childSub => {
    childSub.subscription = result.included.filter(item=>item.type == 'subscription').find(sub => childSub.attributes.subscription_id == sub.id)
  });
  child_subscriptions.sort((a, b) => new Date(a.attributes.created_at) - new Date(b.attributes.created_at))
  tempChild.subscriptions = child_subscriptions
  return tempChild
}
const isEmpty = ref(true)

const currentSub = ref()
async function get_subscription_time(subscription){
  currentSub.value = subscription
  let pack_id = subscription.attributes.subscription_id
  let response = await get_subscription(pack_id)
  let duration = response.result.data.included.find(item=>item.attributes.key == 'duration')
  currentSub.value['subscription']['duration'] = duration.attributes.value
}

const timeDiff = computed(()=>{
  let time = "-"
  if (!currentSub.value){
    return time
  }
  if (currentSub.value.attributes.start_date != null){
    time = subs_time(currentSub.value.attributes.start_date, currentSub.value.subscription.duration)
  }
  return time
})

async function manuallyActivateSubscription(subscription) {
  // Confirm action
  const result = await Swal.fire({
    title: 'Activate Subscription?',
    html: `Are you sure you want to manually activate this subscription?<br><br><strong>${subscription.subscription.attributes.title}</strong>`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, activate it!',
    cancelButtonText: 'Cancel'
  })

  if (!result.isConfirmed) return

  activatingId.value = subscription.id
  
  const response = await activate_child_subscription(subscription.id)
  
  activatingId.value = null

  if (response.error) {
    const errorMsg = response.result?.response?.data?.error || 'Failed to activate subscription'
    swalError(errorMsg)
    return
  }

  swalSuccess('Subscription activated successfully!')
  
  // Refresh the subscription list
  await get_child_subscription()
}

const hasActiveSubscription = computed(() => {
  if (!child.value.subscriptions) return false
  return child.value.subscriptions.some(sub => sub.attributes.status === 'active')
})

const hasPendingSubscriptions = computed(() => {
  if (!child.value.subscriptions) return false
  return child.value.subscriptions.some(sub => 
    sub.attributes.status === 'pending' || sub.attributes.status === null
  )
})
</script>

<template>
  <div class="card">
    <div class="card-header card-no-border">
      <div class="d-flex justify-content-between align-items-center w-100">
        <h5 class="m-0">Subscribed Package</h5>
        <span v-if="hasPendingSubscriptions && !hasActiveSubscription" 
          class="badge badge-warning"
          title="There are pending subscriptions that need activation"
        >
          <i class="fa fa-exclamation-triangle me-1"></i>
          Action Required
        </span>
      </div>
    </div>
    <div class="card-body pt-0">
      <div v-if="isLoading" class="text-center py-3">
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <div v-else-if="isEmpty">
        <p>No subscription for this child yet</p>
        <router-link class="btn btn-light-primary"
          :to="{name: 'subscription'}"
        >
          Subscribe Now
        </router-link>
      </div>
      <div v-else>
        <template v-if="child.subscriptions.length == 0">
          <p>No active subscription</p>
        </template>
        <template v-else>
          <div v-for="sub in child.subscriptions" :key="sub.id" class="mb-3 pb-3 border-bottom">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex flex-column flex-grow-1">
                <div class="">
                  <strong>{{ sub.subscription.attributes.title }}</strong>
                </div>
                <div class="mb-2">
                  <span class="badge text-capitalize"
                    :class="{
                      'badge-light-success': sub.attributes.status == 'active',
                      'badge-light-warning': sub.attributes.status == 'pending' || sub.attributes.status == null,
                      'badge-light-danger': sub.attributes.status == 'expired'
                    }"
                  >
                    {{ sub.attributes.status || 'pending' }}
                  </span>
                  <span v-if="sub.attributes.status == 'active'" 
                    class="badge badge-light-success"
                  >
                    {{ timeDiff, get_subscription_time(sub) }}
                  </span>
                </div>
              </div>

              <!-- Date Display on the Side -->
              <div v-if="sub.attributes.start_date" class="ms-3">
                <div class="bg-light px-3 py-2 rounded d-flex align-items-center">
                  <div class="d-flex flex-column text-center me-2">
                    <span class="text-muted" style="font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px;">Start</span>
                    <span class="text-dark fw-bold" style="font-size: 12px;">
                      {{ formattedDate(sub.attributes.start_date) }}
                    </span>
                  </div>
                  
                  <div class="text-muted opacity-50 mx-2">
                    <i class="fa fa-long-arrow-right"></i>
                  </div>

                  <div class="d-flex flex-column text-center ms-2" v-if="sub.attributes.end_date">
                    <span class="text-muted" style="font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px;">End</span>
                    <span class="text-dark fw-bold" style="font-size: 12px;">
                      {{ formattedDate(sub.attributes.end_date) }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-if="(sub.attributes.status === 'pending' || sub.attributes.status === null) && !hasActiveSubscription" class="ms-3">
                <button 
                  @click="manuallyActivateSubscription(sub)"
                  class="btn btn-sm btn-primary"
                  :disabled="activatingId === sub.id"
                >
                  <span v-if="activatingId === sub.id">
                    <div class="spinner-border spinner-border-sm me-1" role="status">
                      <span class="visually-hidden">Activating...</span>
                    </div>
                  </span>
                  <span v-else>
                    <i class="fa fa-play"></i>
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- Info alert if there are pending subscriptions but an active one exists -->
          <div v-if="hasPendingSubscriptions && hasActiveSubscription" 
            class="alert alert-info mt-3 mb-0"
          >
            <i class="fa fa-info-circle me-2"></i>
            <small>Pending subscriptions will automatically activate when the current subscription expires.</small>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.border-bottom:last-child {
  border-bottom: none !important;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}
</style>